import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Delete, Keyboard } from '@mui/icons-material';
import { Box, IconButton, TextFieldProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import styleVariables from '@/styles/style.module.scss';
import { useMobileState } from '@/mobile/provider/context';
import FakeCaretInput, { FakeCaretInputRef } from './FakeCaret';
import { useScannerTarget } from '../features/register-device/hooks/useScannerTarget';

export interface ScannerInputFieldRef {
    focus: () => void;
    blur: () => void;
}

interface Props extends Omit<TextFieldProps, 'InputProps' | 'value' | 'onChange' | 'inputRef' | 'variant'> {
    value: string;
    setValue: (val: string) => void;
    onClear?: () => void;

    /**
     * Controls whether this input can receive native scanner events.
     */
    scannerEnabled?: boolean;

    /**
     * Called when native PDA / hardware scanner emits barcode.
     *
     * NormalFlow should pass:
     * onScannerScan={handleBarcodeScanned}
     */
    onScannerScan?: (barcode: string) => void | Promise<void>;

    /**
     * Allows parent to override root container style.
     */
    containerSx?: SxProps<Theme>;
    inputTestId?: string;
}

const toSxArray = (sx?: SxProps<Theme>) => {
    if (!sx) return [];

    return Array.isArray(sx) ? sx : [sx];
};

const normalizeInputValue = (nextValue: string) => {
    return nextValue.startsWith('++') ? nextValue : nextValue.toUpperCase();
};

const ScannerInputField = forwardRef<ScannerInputFieldRef, Props>(
    (
        {
            value,
            setValue,
            onClear,
            disabled = false,
            scannerEnabled = true,
            onScannerScan,
            onKeyDown,
            containerSx,
            inputTestId,
        },
        ref,
    ) => {
        const {
            state: { keyboardEnabled },
            dispatch,
        } = useMobileState();

        const realInputRef = useRef<HTMLInputElement | null>(null);
        const fakeInputRef = useRef<FakeCaretInputRef | null>(null);

        const [isFocused, setIsFocused] = useState(false);

        const moveNativeCaretToEnd = useCallback(() => {
            const inputEl = realInputRef.current;
            if (!inputEl) return;

            const end = inputEl.value.length;

            try {
                inputEl.setSelectionRange(end, end);
            } catch {
                // Some WebView/input cases may not support setSelectionRange.
            }

            inputEl.scrollLeft = inputEl.scrollWidth;
        }, []);

        const handleScannerValue = useCallback(
            async (barcode: string) => {
                if (disabled) return;

                if (onScannerScan) {
                    await onScannerScan(barcode);
                    return;
                }

                setValue(normalizeInputValue(barcode));
            },
            [disabled, onScannerScan, setValue],
        );

        const { activateScannerTarget, deactivateScannerTarget } = useScannerTarget({
            enabled: scannerEnabled && !disabled,
            onScan: handleScannerValue,
        });

        const blurField = useCallback(() => {
            setIsFocused(false);
            deactivateScannerTarget();

            if (document.activeElement === realInputRef.current) {
                realInputRef.current?.blur();
            }

            fakeInputRef.current?.blur();
        }, [deactivateScannerTarget]);

        const focusField = useCallback(() => {
            if (disabled) return;

            setIsFocused(true);
            activateScannerTarget();

            if (keyboardEnabled) {
                realInputRef.current?.focus();

                requestAnimationFrame(() => {
                    moveNativeCaretToEnd();
                });

                return;
            }

            fakeInputRef.current?.focus();
        }, [activateScannerTarget, disabled, keyboardEnabled, moveNativeCaretToEnd]);

        useImperativeHandle(
            ref,
            () => ({
                focus: focusField,
                blur: blurField,
            }),
            [blurField, focusField],
        );

        useEffect(() => {
            if (!disabled) return;

            blurField();
        }, [disabled, blurField]);

        useEffect(() => {
            return () => {
                deactivateScannerTarget();
            };
        }, [deactivateScannerTarget]);

        const handleToggleKeyboard = useCallback(
            (event: React.PointerEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();

                if (disabled) return;

                const nextKeyboardEnabled = !keyboardEnabled;
                const shouldRestoreFocus = isFocused;

                dispatch({
                    type: 'SET_KEYBOARD_ENABLED',
                    payload: nextKeyboardEnabled,
                });

                requestAnimationFrame(() => {
                    if (!shouldRestoreFocus || disabled) return;

                    setIsFocused(true);
                    activateScannerTarget();

                    if (nextKeyboardEnabled) {
                        realInputRef.current?.focus();

                        requestAnimationFrame(() => {
                            moveNativeCaretToEnd();
                        });

                        return;
                    }

                    fakeInputRef.current?.focus();
                });
            },
            [activateScannerTarget, disabled, dispatch, isFocused, keyboardEnabled, moveNativeCaretToEnd],
        );

        const handleRealInputFocus = useCallback(() => {
            if (disabled) return;

            setIsFocused(true);
            activateScannerTarget();

            requestAnimationFrame(() => {
                moveNativeCaretToEnd();
            });
        }, [activateScannerTarget, disabled, moveNativeCaretToEnd]);

        const handleRealInputBlur = useCallback(() => {
            setIsFocused(false);
            deactivateScannerTarget();
        }, [deactivateScannerTarget]);

        const handleFakeFocusedChange = useCallback(
            (focused: boolean) => {
                if (disabled) return;

                setIsFocused(focused);

                if (focused) {
                    activateScannerTarget();
                    return;
                }

                deactivateScannerTarget();
            },
            [activateScannerTarget, deactivateScannerTarget, disabled],
        );

        const rootSx = useMemo(
            () => ({
                border: `1px solid ${isFocused ? styleVariables.primaryColor : styleVariables.secondaryColor}`,
                height: 48,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                opacity: disabled ? 0.7 : 1,
                backgroundColor: disabled ? '#f5f5f5' : 'transparent',
                px: 0.75,
                WebkitTapHighlightColor: 'transparent',
            }),
            [disabled, isFocused],
        );

        return (
            <Box sx={[rootSx, ...toSxArray(containerSx)]}>
                <Box
                    sx={{
                        flex: '0 0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <IconButton
                        tabIndex={-1}
                        disabled={disabled}
                        onPointerDown={handleToggleKeyboard}
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                        sx={{
                            WebkitTapHighlightColor: 'transparent',

                            '&:focus, &:focus-visible': {
                                outline: 'none',
                            },
                        }}
                    >
                        <Keyboard
                            sx={{
                                fontSize: 26,
                                color: keyboardEnabled ? styleVariables.primaryColor : styleVariables.darkGrey,
                            }}
                        />
                    </IconButton>
                </Box>

                {keyboardEnabled ? (
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            minWidth: 0,
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            overflow: 'hidden',
                            px: '5px',
                        }}
                    >
                        <input
                            ref={realInputRef}
                            data-testid={inputTestId}
                            disabled={disabled}
                            value={value}
                            onChange={(event) => {
                                setValue(normalizeInputValue(event.target.value));
                            }}
                            onFocus={handleRealInputFocus}
                            onBlur={handleRealInputBlur}
                            onKeyDown={onKeyDown}
                            readOnly={false}
                            inputMode="text"
                            tabIndex={disabled ? -1 : 0}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                font: 'inherit',
                                color: 'inherit',
                                padding: 0,
                                margin: 0,
                                caretColor: '',
                                WebkitTapHighlightColor: 'transparent',
                            }}
                        />
                    </Box>
                ) : (
                    <FakeCaretInput
                        ref={fakeInputRef}
                        value={value}
                        disabled={disabled}
                        focused={isFocused}
                        setFocused={handleFakeFocusedChange}
                        inputTestId={inputTestId}
                    />
                )}

                <Box
                    sx={{
                        flex: '0 0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {onClear && (
                        <IconButton
                            tabIndex={-1}
                            disabled={disabled}
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            onPointerDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();

                                if (disabled) return;

                                onClear();
                            }}
                            sx={{
                                WebkitTapHighlightColor: 'transparent',

                                '&:focus, &:focus-visible': {
                                    outline: 'none',
                                },
                            }}
                        >
                            <Delete
                                sx={{
                                    fontSize: 26,
                                    color: styleVariables.errorColor,
                                }}
                            />
                        </IconButton>
                    )}
                </Box>
            </Box>
        );
    },
);

ScannerInputField.displayName = 'ScannerInputField';

export default ScannerInputField;
